import type { TeenPattiLegal, TeenPattiView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Coins, Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { MySeat, SeatView, useSpeech } from './common';

const CATEGORY: Record<string, string> = { TRAIL: 'Trail', PURE_SEQUENCE: 'Pure sequence', SEQUENCE: 'Sequence', COLOR: 'Colour', PAIR: 'Pair', HIGH_CARD: 'High card' };

export function TeenPattiTable() {
  const t = useT();
  const view = useGame((s) => s.view) as TeenPattiView;
  const legal = useGame((s) => s.legal) as TeenPattiLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const speech = useSpeech();
  const total = view.players.length;
  const me = mySeat !== null ? view.players[mySeat] : null;
  const revealed = view.lastResult?.revealed ?? {};
  const winners = new Set(view.lastResult?.winners ?? []);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const statusTag = (st: string) =>
    st === 'BLIND' ? <span className="tag blind">{t('tp.blindTag')}</span> : st === 'SEEN' ? <span className="tag seen">{t('tp.seenTag')}</span> : st === 'PACKED' ? <span className="tag folded">{t('tp.packedTag')}</span> : null;

  return (
    <>
      <div className="stage">
      <div className="felt" />
      {view.players.map((p, s) => (
        <SeatView
          key={s}
          seat={s}
          info={seats[s]}
          mySeat={mySeat}
          total={total}
          turn={turn}
          speech={speech[s]}
          folded={p?.status === 'PACKED'}
          sub={p ? <Coins amount={p.chips} size={14} /> : null}
          tags={
            <>
              {view.dealer === s && <span className="tag">D</span>}
              {p && view.phase !== 'WAITING' && statusTag(p.status)}
              {winners.has(s) && view.phase === 'HAND_END' && <span className="tag seen">WIN</span>}
            </>
          }
        >
          {p && (revealed[s] ? (
            <div className="shown-cards" aria-label="Shown cards">
              {revealed[s]!.map((c) => (
                <PlayingCard key={c} id={c} size="md" />
              ))}
            </div>
          ) : p.hasCards && p.status !== 'PACKED' && view.phase !== 'WAITING' ? (
            <div className="mini-cards">
              {[0, 1, 2].map((i) => (
                <PlayingCard key={i} faceDown size="sm" />
              ))}
            </div>
          ) : null)}
          {p && p.invested > 0 && view.phase !== 'HAND_END' && (
            <span className="bet">
              <Coins amount={p.invested} size={12} />
            </span>
          )}
          {view.phase === 'HAND_END' && view.lastResult?.categories[s] && <span className="tag">{CATEGORY[view.lastResult.categories[s]!]}</span>}
        </SeatView>
      ))}
      <div className="felt-center centered">
        {view.phase === 'WAITING' ? (
          <div className="center-note">{t('table.waitingPlayers')}</div>
        ) : (
          <>
            <div className="pot" aria-label={`Pot ${view.pot}`}>
              <span className="pot-label">{t('table.pot')}</span>
              <Coins amount={view.phase === 'HAND_END' ? (view.lastResult?.pot ?? 0) : view.pot} size={20} />
            </div>
            <div className="center-note">
              {t('tp.stake', { n: view.stake })} · {t('table.hand', { n: view.handNo })}
            </div>
            {view.lastAction && view.phase !== 'HAND_END' && (
              <div className="center-note" style={{ opacity: 0.85 }}>
                {name(view.lastAction.seat)}: {view.lastAction.action} {view.lastAction.amount ? view.lastAction.amount : ''}
              </div>
            )}
            {view.phase === 'HAND_END' && view.lastResult && (
              <div className="card glass" style={{ textAlign: 'center' }}>
                <strong>🏆 {view.lastResult.winners.map(name).join(', ')}</strong>
                <div className="dim" style={{ fontSize: 'var(--fs-sm)' }}>
                  {view.lastResult.reason === 'ALL_PACKED' ? 'Everyone else packed' : view.lastResult.reason === 'POT_LIMIT' ? 'Pot limit reached — showdown' : 'Show'}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {view.sideShowReveal && Object.keys(view.sideShowReveal).length > 0 && view.phase === 'BETTING' && (
        <div className="card glass" style={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 25, textAlign: 'center' }}>
          {Object.entries(view.sideShowReveal).map(([seat, cards]) => (
            <div key={seat}>
              <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{name(Number(seat))}</div>
              <div className="row" style={{ gap: 4, justifyContent: 'center' }}>
                {cards.map((c) => (
                  <PlayingCard key={c} id={c} size="sm" />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={me ? <Coins amount={me.chips} size={14} /> : null} tags={me && view.phase !== 'WAITING' ? statusTag(me.status) : null} />
      </div>
      <div className="table-bottom">
        <div className="my-cards" aria-label="Your cards">
          {view.hand.length === 3
            ? view.hand.map((c, i) => <PlayingCard key={c} id={c} size="lg" dealIndex={i} />)
            : me?.hasCards && me.status !== 'PACKED'
              ? [0, 1, 2].map((i) => <PlayingCard key={i} faceDown size="lg" dealIndex={i} />)
              : null}
        </div>
        {legal && (
          <div className="action-bar">
            {legal.canSee && (
              <button className="btn" disabled={acting} onClick={() => void act({ type: 'see' })}>
                👀 {t('tp.see')}
              </button>
            )}
            {legal.canPack && (
              <button className="btn danger" disabled={acting} onClick={() => void act({ type: 'pack' })}>
                {t('tp.pack')}
              </button>
            )}
            {legal.call !== null && (
              <button className="btn primary" disabled={acting} onClick={() => void act({ type: 'bet', raise: false })}>
                {me?.status === 'BLIND' ? t('tp.blind', { n: legal.call }) : t('tp.chaal', { n: legal.call })}
              </button>
            )}
            {legal.raise !== null && (
              <button className="btn gold" disabled={acting} onClick={() => void act({ type: 'bet', raise: true })}>
                {t('tp.raise', { n: legal.raise })}
              </button>
            )}
            {legal.show !== null && (
              <button className="btn" disabled={acting} onClick={() => void act({ type: 'show' })}>
                {t('tp.show', { n: legal.show })}
              </button>
            )}
            {legal.sideShow !== null && (
              <button className="btn" disabled={acting} onClick={() => void act({ type: 'sideshow' })}>
                {t('tp.sideShow', { n: legal.sideShow })}
              </button>
            )}
          </div>
        )}
      </div>
      <Modal open={Boolean(legal?.respondSideShow)} onClose={() => void act({ type: 'sideshowRespond', accept: false })} title={t('tp.sideShowAsk', { name: view.sideShow ? name(view.sideShow.from) : '' })}>
        <div className="row">
          <button className="btn ghost grow" onClick={() => void act({ type: 'sideshowRespond', accept: false })}>
            {t('tp.decline')}
          </button>
          <button className="btn primary grow" onClick={() => void act({ type: 'sideshowRespond', accept: true })}>
            {t('tp.accept')}
          </button>
        </div>
      </Modal>
    </>
  );
}
